import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";

import AuthService from "../services/auth.service";
import OrderService from "../services/order.service";
import { Link, useNavigate } from "react-router-dom";
import { money, statusBadge } from "../utils/order";
import { useTranslation } from "react-i18next";
import Spinner from "./Spinner";

const Profile = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const currentUser = AuthService.getCurrentUser();

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [savingPwd, setSavingPwd] = useState(false);

  useEffect(() => {
    if (!currentUser) {
      navigate("/", { replace: true });
      return;
    }
    OrderService.myOrders()
      .then((res) => setOrders(res.data || []))
      .catch((err) =>
        toast.error(
          (err.response && err.response.data && err.response.data.message) ||
            err.message
        )
      )
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!currentUser) return null;

  const initial = (currentUser.username || "?").charAt(0).toUpperCase();
  const roles = (currentUser.roles || []).map((r) =>
    r.replace(/^ROLE_/, "").toLowerCase()
  );

  const mismatch = confirmPassword.length > 0 && newPassword !== confirmPassword;

  const handleChangePassword = (e) => {
    e.preventDefault();
    if (newPassword.length < 6 || newPassword.length > 40) {
      toast.error(t("profile.passwordLength"));
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error(t("auth.passwordMismatch"));
      return;
    }
    if (newPassword === currentPassword) {
      toast.error(t("profile.samePassword"));
      return;
    }
    setSavingPwd(true);
    AuthService.changePassword(currentPassword, newPassword, confirmPassword)
      .then(() => {
        toast.success(t("profile.passwordChanged"));
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
      })
      .catch((err) =>
        toast.error(
          (err.response && err.response.data && err.response.data.message) ||
            err.message
        )
      )
      .finally(() => setSavingPwd(false));
  };

  const logOut = () => {
    AuthService.logout();
    navigate("/", { replace: true });
  };

  return (
    <div className="container my-4">
      <div className="row g-4">
        {/* Account card */}
        <div className="col-lg-4">
          <div className="card shadow-sm">
            <div className="card-body text-center">
              <div
                className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center mx-auto mb-3"
                style={{ width: 88, height: 88, fontSize: 40, fontWeight: 600 }}
              >
                {initial}
              </div>
              <h4 className="mb-1">{currentUser.username}</h4>
              <p className="text-muted mb-3">{currentUser.email}</p>
              <div className="d-flex flex-wrap gap-1 justify-content-center mb-3">
                {roles.map((role) => (
                  <span key={role} className="badge bg-secondary text-capitalize">
                    {role}
                  </span>
                ))}
              </div>
              <button className="btn btn-outline-danger btn-sm" onClick={logOut}>
                {t("profile.logout")}
              </button>
            </div>
          </div>

          <div className="card shadow-sm mt-3">
            <div className="card-header fw-semibold">{t("profile.account")}</div>
            <ul className="list-group list-group-flush">
              <li className="list-group-item d-flex justify-content-between">
                <span className="text-muted">{t("profile.id")}</span>
                <span>{currentUser.id}</span>
              </li>
              <li className="list-group-item d-flex justify-content-between">
                <span className="text-muted">{t("profile.username")}</span>
                <span>{currentUser.username}</span>
              </li>
              <li className="list-group-item d-flex justify-content-between">
                <span className="text-muted">{t("profile.email")}</span>
                <span>{currentUser.email}</span>
              </li>
            </ul>
          </div>

          <div className="card shadow-sm mt-3">
            <div className="card-header fw-semibold">{t("profile.changePassword")}</div>
            <div className="card-body">
              <form onSubmit={handleChangePassword}>
                <div className="mb-3">
                  <label htmlFor="currentPassword" className="form-label">
                    {t("profile.currentPassword")}
                  </label>
                  <input
                    id="currentPassword"
                    type="password"
                    className="form-control"
                    autoComplete="current-password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    required
                  />
                </div>
                <div className="mb-3">
                  <label htmlFor="newPassword" className="form-label">
                    {t("profile.newPassword")}
                  </label>
                  <input
                    id="newPassword"
                    type="password"
                    className="form-control"
                    autoComplete="new-password"
                    minLength={6}
                    maxLength={40}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                  />
                </div>
                <div className="mb-3">
                  <label htmlFor="confirmPassword" className="form-label">
                    {t("profile.confirmNewPassword")}
                  </label>
                  <input
                    id="confirmPassword"
                    type="password"
                    className={`form-control${mismatch ? " is-invalid" : ""}`}
                    autoComplete="new-password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                  />
                  {mismatch && (
                    <div className="invalid-feedback">{t("auth.passwordMismatch")}</div>
                  )}
                </div>
                <button
                  type="submit"
                  className="btn btn-primary w-100"
                  disabled={savingPwd || mismatch}
                >
                  {savingPwd ? t("profile.saving") : t("profile.changePassword")}
                </button>
              </form>
            </div>
          </div>
        </div>

        {/* Order history */}
        <div className="col-lg-8">
          <div className="card shadow-sm">
            <div className="card-header d-flex justify-content-between align-items-center">
              <span className="fw-semibold">{t("profile.orderHistory")}</span>
              <Link to="/orders" className="btn btn-sm btn-outline-primary">
                {t("orders.title")}
              </Link>
            </div>
            <div className="card-body">

              {loading ? (
                <Spinner small />
              ) : orders.length === 0 ? (
                <div className="text-center py-4">
                  <p className="text-muted">{t("orders.empty")}</p>
                  <Link to="/catalog" className="btn btn-primary">
                    {t("orders.browseProducts")}
                  </Link>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0">
                    <thead>
                      <tr>
                        <th style={{ width: 60 }}>#</th>
                        <th>{t("orders.date")}</th>
                        <th>{t("orders.items")}</th>
                        <th style={{ width: 130 }}>{t("orders.total")}</th>
                        <th style={{ width: 120 }}>{t("orders.status")}</th>
                        <th style={{ width: 80 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {orders.map((o) => {
                        const meta = statusBadge(o.status);
                        return (
                          <tr key={o.id}>
                            <td>{o.id}</td>
                            <td>{new Date(o.createdAt).toLocaleString()}</td>
                            <td>
                              {(o.items || []).reduce(
                                (n, it) => n + it.quantity,
                                0
                              )}
                            </td>
                            <td className="fw-bold">{money(o.total_amount)}</td>
                            <td>
                              <span className={`badge ${meta.cls}`}>
                                {t(`orderStatus.${o.status}`, meta.label)}
                              </span>
                            </td>
                            <td>
                              <Link
                                to={`/orders/${o.id}`}
                                className="btn btn-sm btn-outline-primary"
                              >
                                {t("orders.view")}
                              </Link>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
